import { Grid } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { connections, useSearchIntegrationSources } from '../../../../../api/integrations'
import EmptySection from '../../../../atoms/EmptySection'
import SydButton from '../../../../commonDesign/Button'
import Card from '../../../../molecules/Card'
import Loading from '../../../../molecules/Loading'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import AdminPage from '../../../admin/shared/AdminPage'
import ConnectionHeader from './ConnectionHeader'
import ConnectionSettings from './ConnectionSettings'
import ConnectionsListSidepanel from './ConnectionsListSidepanel'
import CreateConnectionDialog from './CreateConnectionDialog'
import DelegationConfirmationDialog from './DelegationConfirmationDialog'
import IntegrationSourceList from './IntegrationSourceList'

const useSelectedConnectionState = (data) => {
  const { connectionCode } = useParams()
  const selectedConnection = useMemo(() => {
    return (data?.connections || []).find(c => c.code.toLowerCase() === connectionCode?.toLowerCase())
  }, [connectionCode, data])

  const history = useHistory()
  const setSelectedConnection = useCallback((conn) => {
    if (conn) {
      history.replace(`/admin/denali/integrator/connections/${conn.code}`)
    } else {
      history.replace('/admin/denali/integrator/connections')
    }
  }, [history])

  return [selectedConnection, setSelectedConnection]
}

function ConnectionsList ({ root }) {
  const createConnectionRef = useRef(null)
  const delegationConfirmationRef = useRef(null)
  const maybeConfirm = useCallback((response) => {
    if (response.connectionRequestId) {
      delegationConfirmationRef.current?.open(response.connectionRequestId)
    }
  }, [])

  const { data, isLoading, isFetching } = connections.useListIntegrationConnections()
  const [selectedConnection, setSelectedConnection] = useSelectedConnectionState(data)
  // User-auth-only domains (e.g. `orion-connect`) have `hasSync: false` and
  // skip the sources panel.
  const canShowSources = !!selectedConnection && selectedConnection.hasSync !== false
  const sourceQuery = useMemo(() => ({ filters: { connectionId: selectedConnection?.connectionId } }), [selectedConnection])
  const { data: isData, isLoading: integrationSourcesLoading } = useSearchIntegrationSources(sourceQuery, { enabled: canShowSources })

  if (isLoading) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className='flex justify-between items-center'>
            <div className='font-size-lg font-bold'>Connections</div>
            <SydButton size='sm' variant='primary' icon='add' onClick={() => createConnectionRef.current.open()} processing={isFetching}>Create</SydButton>
          </div>
        </Grid>
        <Grid item xs={12} md={3}>
          <ConnectionsListSidepanel
            root={root}
            connections={data}
            selectedConnection={selectedConnection}
            onSetSelectedConnection={setSelectedConnection}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          {!selectedConnection ? (
            <FadeIn>
              <Card>
                <EmptySection title='Select a connection to view details' description='' />
              </Card>
            </FadeIn>
          ) : (canShowSources && integrationSourcesLoading) ? (
            <FadeIn>
              <Loading />
            </FadeIn>
          ) : (
            <FadeIn>
              <div className='flex flex-col gap-lg'>
                <ConnectionHeader connection={selectedConnection} />
                <ConnectionSettings connection={selectedConnection} />
                {canShowSources && (
                  <IntegrationSourceList
                    root={root}
                    connectionId={selectedConnection.connectionId}
                    sources={(isData?.data || [])}
                  />
                )}
              </div>
            </FadeIn>
          )}
        </Grid>
      </Grid>
      <CreateConnectionDialog ref={createConnectionRef} onComplete={maybeConfirm} />
      <DelegationConfirmationDialog ref={delegationConfirmationRef} />
    </AdminPage>
  )
}

ConnectionsList.propTypes = {
  root: PropTypes.any
}

export default ConnectionsList
